import { Injectable, OnDestroy, inject } from "@angular/core";
import { LgFilterSet } from "@logex/framework/lg-filterset";
import { FilterFactory, FilterFactoryCreator } from "@shared/filter-factory";
import { argsExcept, getFilterIds } from "@shared/helpers/filter";
import { Filters, PageKeys } from "@shared/types";
import { Observable } from "rxjs";
import { LoaderArgumentsMap } from "../load-manager";
import { IVariationFilterProvider } from "./variation-filter-provider.types";
import { Dictionary } from "lodash";
import { AppDefinitions } from "@shared/app-definitions.service";

@Injectable()
export class KpiFilterProvider implements IVariationFilterProvider {
    protected _appDefinition = inject(AppDefinitions);
    protected _filterFactory = inject(FilterFactory);

    readonly defaultReferencesParams = {
        referenceId: 4,
        weightType: 1
    };

    init(
        source: (filterName: string, argsOverride?: LoaderArgumentsMap) => Observable<any>,
        extParams: Dictionary<any>
    ): FilterFactoryCreator<any, {}> {
        const creator = this._filterFactory
            .define()
            .diagnosisGroupFilter({
                source: () => source(Filters.DIAGNOSIS_GROUP)
            })
            .specialismFilter({
                source: () => source(Filters.SPECIALISM)
            });

        for (const key in extParams) {
            if (Object.prototype.hasOwnProperty.call(extParams, key)) {
                const filterDef = creator.getFilter(key);
                if (filterDef) {
                    Object.assign(filterDef, extParams[key]);
                }
            }
        }
        return creator;
    }

    public get pagekey(): string {
        return PageKeys.KPI_PAGE_KEY;
    }

    _getLocalizedFilters() {
        return {
            [Filters.DIAGNOSIS_GROUP]: {
                name: "FW._Filters.DiagnosisGroup",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("diagnosisGroup", value);
                }
            },
            [Filters.SPECIALISM]: {
                name: "FW._Filters.Specialism",
                options: (value: number | string) => {
                    return this._appDefinition.getDisplayName("specialism", value);
                }
            }
        };
    }

    _getLoaderArguments(filterContext: LgFilterSet<any, any>): LoaderArgumentsMap {
        return {
            [Filters.DIAGNOSIS_GROUP]: () => getFilterIds(Filters.DIAGNOSIS_GROUP, filterContext),
            [Filters.SPECIALISM]: () => getFilterIds(Filters.SPECIALISM, filterContext)
        } as LoaderArgumentsMap;
    }

    _createLoaders(
        args: LoaderArgumentsMap,
        action: (filterName: string, args: LoaderArgumentsMap) => any
    ): any {
        return {
            ...action(Filters.DIAGNOSIS_GROUP, argsExcept(args, Filters.DIAGNOSIS_GROUP)),
            ...action(Filters.SPECIALISM, argsExcept(args, Filters.SPECIALISM))
        };
    }
}
